import { CustomItemFormStructure, FlexyForm } from '@europrocurement/flexy-form';
import { type DossierOpenSearch, useFormaliteService } from '@europrocurement/l2d-domain';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Uploader, useUploader } from '@europrocurement/flexy-components';
import { type DocumentGed } from '../../../hooks/useDossierDocumentFilesApi';

type DossierDocumentGedCreateFormProps = {
    dossier: DossierOpenSearch;
    reglementId: number;
    onSuccess: (createdFiles: DocumentGed[]) => void;
    onClose: () => void;
};
const DossierDocumentGedCreateForm: React.FunctionComponent<DossierDocumentGedCreateFormProps> =
    function ({ onSuccess, onClose, dossier, reglementId }) {
        const { files } = useUploader();
        const { documentGedApi } = useFormaliteService();

        const { enqueueSnackbar } = useSnackbar();

        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

        const formContext = useForm({ mode: 'onTouched' });

        const handleSubmit = useCallback(async () => {
            if (!dossier) {
                enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
                return;
            }
            if (!dossier.prescripteurId) {
                enqueueSnackbar('Prescripteur non trouvé', { variant: 'error' });
                return;
            }
            if (!dossier.societeId) {
                enqueueSnackbar('Société non trouvée', { variant: 'error' });
                return;
            }

            setIsSubmitting(true);
            try {
                const gedPromises = await Promise.all(
                    files.map(async (file) =>
                        documentGedApi.apiDocumentgedFilePost({
                            file,
                            fileName: file.name,
                            idPrescripteur: dossier.prescripteurId as number,
                            idSociete: dossier.societeId as number,
                            idDossierFormalite: dossier.id as number,
                            idReglement: reglementId,
                        }),
                    ),
                );
                const createdFiles = gedPromises.flat().map((res) => res.data);

                onSuccess(createdFiles);
            } catch (err) {
                console.log(err);
                enqueueSnackbar("Une erreur est survenue lors de l'envoi du formulaire", {
                    variant: 'error',
                });
                return;
            } finally {
                setIsSubmitting(false);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dossier, files, reglementId]);

        const formProps = {
            formContext,
            formObject: {},
            onSubmit: handleSubmit,
            gridProps: { sx: { gap: '8px' } },
            submitButton: {
                label: 'Envoyer',
                displayed: false,
            },
            isSubmitting,
        };

        const formStructure = [
            {
                type: 'customItem',
                name: 'attachmentsUploader',
                renderField: () => (
                    <Box sx={{ display: 'grid', gap: '4px' }}>
                        <Typography variant="l2dlabel">Document</Typography>
                        <Uploader
                            label="Document"
                            sx={{ margin: 0 }}
                            fileUlpoadOverides={{ height: '160px' }}
                        />
                    </Box>
                ),
            } as CustomItemFormStructure,
            {
                type: 'customItem',
                name: 'buttonsFooter',
                renderField: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            color="primary"
                            sx={{
                                marginTop: '2rem',
                                marginBottom: '16px',
                                color: 'text.gris70',
                                padding: '16px 24px',
                                fontSize: '16px',
                            }}
                            onClick={() => onClose()}
                        >
                            Annuler
                        </Button>
                        {isSubmitting ? (
                            <Button
                                variant="contained"
                                type="button"
                                sx={{
                                    marginTop: '2rem',
                                    marginBottom: '16px',
                                    padding: '16px 24px',
                                    fontSize: '16px',
                                }}
                            >
                                <CircularProgress
                                    color="inherit"
                                    size="16px"
                                />
                            </Button>
                        ) : (
                            <Box sx={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    name="submitDocGed"
                                    disabled={isSubmitting}
                                    sx={{
                                        marginTop: '2rem',
                                        marginBottom: '16px',
                                        padding: '16px 24px',
                                        fontSize: '16px',
                                    }}
                                >
                                    Valider
                                </Button>
                            </Box>
                        )}
                    </Box>
                ),
            } as CustomItemFormStructure,
        ];
        return (
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
        );
    };

export default DossierDocumentGedCreateForm;
